// pages/overlay/TitleCardStat.jsx
// Supporting title card #2 — credentials/stat reveal.
// Upper-third tinted-band overlay. Mirrors the hero TitleCard pattern:
//   /overlay/titlecard-stat          — preview, auto-loops
//   /overlay/titlecard-stat?render   — frame-by-frame WebM download
//   ?bg=green                        — chroma-key background mode
//
// Animation arc (2.5s):
//   0.0–0.4s  navy band fades in
//   0.3–0.8s  Line 1 (stat) fades + translates up
//   0.6–1.1s  Line 2 (credit) fades + translates up
//   1.1–2.0s  held
//   2.0–2.5s  whole card fades out

import { useEffect, useRef, useState } from "react";

const W = 1080;
const H = 1920;
const FPS = 60;
const TOTAL_MS = 2500;
const TOTAL_FRAMES = Math.ceil((TOTAL_MS / 1000) * FPS);

// Brand palette
const ORANGE = "#f57600";
const GOLD   = "#fcb31f";
const CREAM  = "#ffdaad";

// Band placement — upper third
const BAND_CX = W / 2;
const BAND_CY = 580;
const BAND_W  = W - 120;  // 60px margin each side
const BAND_H  = 290;
const BAND_R  = 26;

// Timing (ms)
const BAND_START    = 0;
const BAND_DUR      = 400;
const LINE1_START   = 300;
const LINE1_DUR     = 500;
const LINE2_START   = 600;
const LINE2_DUR     = 500;
const OUT_START     = 2000;
const OUT_DUR       = 500;

// ── EASING ───────────────────────────────────────────────────────────────────
const clamp01 = (t) => Math.max(0, Math.min(1, t));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// ── HELPERS ──────────────────────────────────────────────────────────────────
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── BAND ─────────────────────────────────────────────────────────────────────
// Semi-transparent navy strip — footage tints through slightly. Gold hairlines
// top and bottom keep it on-brand without a full badge frame.
function drawBand(ctx, alpha) {
  if (alpha <= 0) return;
  const x = BAND_CX - BAND_W / 2;
  const y = BAND_CY - BAND_H / 2;

  ctx.save();
  ctx.globalAlpha = alpha;

  // Soft shadow under the band so it lifts off underlying footage
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 10;
  ctx.fillStyle = "rgba(29, 82, 160, 0.92)";
  roundedRect(ctx, x, y, BAND_W, BAND_H, BAND_R);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Gold hairlines top + bottom, inset from the band edges
  ctx.strokeStyle = `rgba(252, 179, 31, 0.75)`;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + 40, y + 14);
  ctx.lineTo(x + BAND_W - 40, y + 14);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + 40, y + BAND_H - 14);
  ctx.lineTo(x + BAND_W - 40, y + BAND_H - 14);
  ctx.stroke();

  ctx.restore();
}

// ── TEXT LINE 1 — STAT ───────────────────────────────────────────────────────
// Split into two lines so the stat fits the band width and gets more punch.
function drawLine1(ctx, alpha, offsetY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "400 50px 'Chango', serif";
  ctx.fillStyle = CREAM;
  ctx.fillText("HUNDREDS OF MILLIONS", BAND_CX, BAND_CY - 50);
  ctx.fillText("OF VIEWS ONLINE",      BAND_CX, BAND_CY + 12);
  ctx.restore();
}

// ── TEXT LINE 2 — CREDIT ──────────────────────────────────────────────────────
// Lobster Two italic with orange bullet between phrase and year.
function drawLine2(ctx, alpha, offsetY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(0, offsetY);
  ctx.textBaseline = "alphabetic";
  // Fredoka 600 all caps, letter-spaced editorial tag.
  // Same treatment as the GTS card subtitle for typographic family cohesion.
  ctx.font = "600 24px Fredoka, sans-serif";
  ctx.letterSpacing = "3px";

  const partA = "ORIGINATOR OF THE FORM";
  const partB = "·";
  const partC = "2009";
  const gap = ctx.measureText("  ").width;
  ctx.textAlign = "left";
  const wA = ctx.measureText(partA).width;
  const wB = ctx.measureText(partB).width;
  const wC = ctx.measureText(partC).width;
  const totalW = wA + gap + wB + gap + wC;
  const x0 = BAND_CX - totalW / 2;
  const xB = x0 + wA + gap;
  const xC = xB + wB + gap;
  const y  = BAND_CY + 90;

  ctx.fillStyle = CREAM;
  ctx.fillText(partA, x0, y);
  ctx.fillStyle = ORANGE;
  ctx.fillText(partB, xB, y);
  ctx.fillStyle = CREAM;
  ctx.fillText(partC, xC, y);

  ctx.letterSpacing = "0px";
  ctx.restore();
}

// ── FRAME DRAWING ────────────────────────────────────────────────────────────
function drawFrame(ctx, frameNum, bgMode) {
  const ms = (frameNum / FPS) * 1000;

  ctx.clearRect(0, 0, W, H);
  if (bgMode === "green") {
    ctx.fillStyle = "#00FF00";
    ctx.fillRect(0, 0, W, H);
  }

  // Global out-fade multiplier
  let outFade = 1;
  if (ms >= OUT_START && ms < OUT_START + OUT_DUR) {
    outFade = 1 - easeOutCubic((ms - OUT_START) / OUT_DUR);
  } else if (ms >= OUT_START + OUT_DUR) {
    return;
  }

  // Band fade-in
  const bandP = clamp01((ms - BAND_START) / BAND_DUR);
  const bandAlpha = easeOutCubic(bandP) * outFade;
  drawBand(ctx, bandAlpha);

  // Line 1
  const line1P = clamp01((ms - LINE1_START) / LINE1_DUR);
  if (line1P > 0) {
    const eased = easeOutCubic(line1P);
    drawLine1(ctx, eased * outFade, (1 - eased) * 20);
  }

  // Line 2
  const line2P = clamp01((ms - LINE2_START) / LINE2_DUR);
  if (line2P > 0) {
    const eased = easeOutCubic(line2P);
    drawLine2(ctx, eased * outFade, (1 - eased) * 20);
  }
}

// ── ASSET LOADING ────────────────────────────────────────────────────────────
async function ensureFontsLoaded() {
  if (!document.fonts) return;
  await Promise.all([
    document.fonts.load("400 50px 'Chango'"),
    document.fonts.load("600 24px Fredoka"),
  ]);
}

// ── PREVIEW MODE ─────────────────────────────────────────────────────────────
function PreviewMode({ bgMode }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => { ensureFontsLoaded().then(() => setReady(true)); }, []);

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;
    let raf;
    function tick() {
      drawFrame(ctx, frame, bgMode);
      frame++;
      if (frame > TOTAL_FRAMES + 60) frame = 0;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready, bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxWidth: "100%", maxHeight: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }} />
      <div style={{ position: "absolute", bottom: "1rem", left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", fontFamily: "system-ui", textAlign: "center", lineHeight: 1.5 }}>
        Stat Card · bg={bgMode} · auto-loops<br />
        <span style={{ opacity: 0.7 }}>?render to download · ?bg=green for chroma-key</span>
      </div>
    </div>
  );
}

// ── RENDER MODE ──────────────────────────────────────────────────────────────
function RenderMode({ bgMode }) {
  const [status, setStatus] = useState("Loading fonts...");
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureFontsLoaded();
      if (cancelled) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      setStatus("Rendering frames...");
      const stream = canvas.captureStream(0);
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 12_000_000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.start();

      for (let f = 0; f <= TOTAL_FRAMES + 5; f++) {
        drawFrame(ctx, f, bgMode);
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        await new Promise((r) => setTimeout(r, 1000 / FPS));
        if (f % 30 === 0) setStatus(`Rendering frame ${f}/${TOTAL_FRAMES}...`);
      }

      ctx.clearRect(0, 0, W, H);
      const track = stream.getVideoTracks()[0];
      if (track.requestFrame) track.requestFrame();
      await new Promise((r) => setTimeout(r, 100));

      recorder.stop();
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `stat-card-${bgMode}.webm`;
        a.click();
        setStatus(`Downloaded stat-card-${bgMode}.webm`);
      };
    })();
    return () => { cancelled = true; };
  }, [bgMode]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#0d1833", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.5rem", fontFamily: "system-ui" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxHeight: "75vh", maxWidth: "90vw", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8 }} />
      <div style={{ color: GOLD, fontSize: "1.1rem", fontWeight: 700 }}>{status}</div>
    </div>
  );
}

// ── ROUTER ───────────────────────────────────────────────────────────────────
export default function TitleCardStat() {
  const params = new URLSearchParams(window.location.search);
  const isRender = params.has("render");
  const bgMode = params.get("bg") === "green" ? "green" : "transparent";
  return isRender ? <RenderMode bgMode={bgMode} /> : <PreviewMode bgMode={bgMode} />;
}
